import { articleApi, companyApi, ECompactWordType, type CompanyImageVo, type CompanyVo } from '@/axios/company';
import { useRequest } from 'ahooks';
import { Button, Checkbox, Flex, Form, Input, InputNumber, message, Radio, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import styles from './index.module.less';
import CommonModalFactory from '@/plugins/ModalFactory';
import SVGIcon from '@/components/SVGIcon';
import SVGDeleteTime from '@/assets/imgs/deleteTime.svg?react';
import ArticleList from './List';
import AccessButton from '@/components/AccessButton';
import CreateButton from '@/components/Base/CreateButton';
import BaseCollapse from '@/components/Base/Collapse';
import CustomTreeSelect from '@/components/Base/TreeSelect';
import AddIcon from '@/assets/icons/add.svg?react';
import ArticleTypeSelector from '@/components/Form/ArticleTypeSelector';
import ArticleTemplateSelector from '@/components/Form/ArticleTemplateSelector';
import ChannelMediaBoxSelector from '@/components/Form/ChannelMediaBoxSelector';
import { useStore } from '@/stores';
import { contains } from '@/utils';
import ArticleUploadModal from './ArticleUploadModal';

type ArticleProps = {
  companyInfo?: Pick<CompanyVo, 'companyId' | 'companyName'>;
};

/**
 * 企业文章页，汇总确认后刷新文章列表及有权查看的已发布成本。
 * @param {ArticleProps} props 当前企业资料。
 * @returns {JSX.Element} 企业文章管理界面。
 */
const Article = ({ companyInfo }: ArticleProps) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const companyId = companyInfo?.companyId || new URLSearchParams(window.location.search).get('companyId') || '';
  const articleListRef = useRef<any>();
  const { data: staticBase, runAsync: fetchStaticBase } = useRequest(() => companyApi.getStaticsBase(companyId), {
    manual: true,
  });
  const userStore = useStore('user');
  const { data: fallbackCompanyInfo } = useRequest(() => companyApi.get(companyId), {
    ready: !!companyId && !companyInfo?.companyName,
    refreshDeps: [companyId, companyInfo?.companyName],
  });
  const currentCompanyName = companyInfo?.companyName || fallbackCompanyInfo?.companyName;
  /**
   * 更新有权限查看的已发布成本，并接住查询失败，避免产生未处理的 Promise。
   * @returns {void} 异步查询由现有 Axios 错误提示和 useRequest 状态负责。
   */
  function refreshPublishedCost(): void {
    if (contains(userStore.info?.permissions || [], ['company:article:statics'])) {
      void fetchStaticBase().catch(() => undefined);
    }
  }

  /**
   * 汇总确认后关闭上传流程，回到列表第一页并保留搜索筛选，同步更新成本。
   * 所有终态均查询实际数据，待核实结果也不触发任何重新上传。
   * @returns {void} 关闭弹窗并发起页面数据刷新。
   */
  function confirmImportResult(): void {
    setUploadOpen(false);
    articleListRef.current?.refreshList();
    refreshPublishedCost();
  }

  useEffect(() => {
    refreshPublishedCost();
  }, [companyId, userStore.info]);
  return (
    <div className={styles.sections}>
      {uploadOpen && (
        <ArticleUploadModal
          companyId={companyId}
          companyName={currentCompanyName}
          onClose={() => setUploadOpen(false)}
          onConfirm={confirmImportResult}
        />
      )}
      <ArticleList
        ref={articleListRef}
        btns={
          <>
            <AccessButton permission={['company:article:create']}>
              <CreateButton
                text="生成文章"
                onClick={() => {
                  openCreateArticleModal(
                    {
                      companyId,
                      strategy: 'MANUAL',
                    },
                    undefined,
                    (_) => {
                      articleListRef.current?.refreshList();
                    }
                  );
                }}
              />
            </AccessButton>
            <AccessButton permission={['company:article:create']}>
              <CreateButton
                text="开始优化"
                onClick={() => {
                  openCreateArticleModal(
                    {
                      companyId,
                      strategy: 'TEMPLATE',
                    },
                    undefined,
                    (_) => {
                      articleListRef.current?.refreshList();
                    }
                  );
                }}
              />
            </AccessButton>
            <AccessButton permission={['company:article:upload']}>
              <CreateButton
                text="上传文章"
                onClick={() => {
                  setUploadOpen(true);
                }}
              />
            </AccessButton>
            {staticBase && (
              <Space>
                <span>当前已发布成本：</span>
                <span>{staticBase?.articleAmount ?? 0}</span>
              </Space>
            )}
          </>
        }
      />
    </div>
  );
};
export default Article;
/** 编辑企业资料或者企业官网 */
export const openCompanyInformationModal = CommonModalFactory<{
  companyId: string;
  content: string;
  propKey?: string;
}>(
  {
    width: 1000,
  },
  ({ data, setOkFn }) => {
    const { propKey = 'information', companyId } = data;
    const [content, setContent] = useState(data?.content);
    useEffect(() => {
      setOkFn(async () => {
        if (propKey === 'information') {
          await companyApi.updateInformation({
            companyId: companyId as string,
            information: content,
          });
          return;
        }
        await companyApi.update({
          companyId: companyId as string,
          [propKey]: content,
        });
      });
    }, [content, propKey, companyId, setOkFn]);
    return (
      <div className={styles.markdownContainer}>
        <Input.TextArea style={{ height: '100%' }} value={content} onChange={(e) => setContent(e.target.value)} />
      </div>
    );
  }
);

export const openEditCompetitorModal = CommonModalFactory<{
  competitors: string[];
  coreWords?: string[];
  companyId: string;
}>(
  {
    title: '编辑竞品',
    width: 1000,
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '16px',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const [generateFormInstance] = Form.useForm();
    const [generating, setGenerating] = useState(false);
    const [deleteIds, setDeleteIds] = useState<string[]>([]);
    const generate = async () => {
      const values = await generateFormInstance.validateFields();
      setGenerating(true);
      try {
        const res = await articleApi.createCompetitor({
          companyId: data?.companyId || '',
          coreWords: values.coreWords || [],
          count: values.count || 1,
          region: values.region || '',
          scope: values.scope || '',
        });
        const competitors = form.getFieldValue('competitors') || [];
        form.setFieldsValue({
          competitors: [...res, ...competitors],
        });
      } finally {
        setGenerating(false);
      }
    };
    const GenForm = () => {
      return (
        <Form form={generateFormInstance} labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
          <Form.Item name="count" label="竞品数量" rules={[{ required: true, message: '请输入竞品数量' }]}>
            <InputNumber placeholder="请输入竞品数量1-30之间" style={{ width: 200 }} min={1} max={30} />
          </Form.Item>
          <Form.Item label="使用核心词" name="coreWords" rules={[{ required: true, message: '请选择核心词' }]}>
            <Checkbox.Group options={data?.coreWords?.map((word) => ({ label: word, value: word }))} />
          </Form.Item>
          <Form.Item name="region" label="地域">
            <Input placeholder="请输入地域" />
          </Form.Item>
          <Form.Item name="scope" label="规模">
            <Input placeholder="请输入规模" />
          </Form.Item>
          <Button type="primary" onClick={generate} loading={generating}>
            生成
          </Button>
        </Form>
      );
    };
    useEffect(() => {
      form.setFieldsValue({
        competitors: data?.competitors || [],
      });
    }, [data]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const competitorNames = (values.competitors || [])
          .map((item: any) => (item?.competitorName || '').trim().toLowerCase())
          .filter(Boolean);
        if (new Set(competitorNames).size !== competitorNames.length) {
          message.warning('竞品名称不能重复');
          throw new Error('竞品名称不能重复');
        }
        await articleApi.updateCompetitor({
          add: (values.competitors || []).filter((item: any) => !item.id),
          companyId: data?.companyId || '',
          modify: (values.competitors || []).filter((item: any) => item.id),
          deleteIds: deleteIds,
        });
      });
    }, [data, form, setOkFn, deleteIds]);
    return (
      <div>
        <Form form={form}>
          <BaseCollapse
            items={[
              {
                key: '1',
                label: '生成竞品',
                children: <GenForm />,
              },
            ]}
            defaultActiveKey={['1']}
          />
          <Form.List name="competitors">
            {(fields, { add, remove }) => (
              <>
                <Button
                  type="link"
                  onClick={() => {
                    add('', 0);
                  }}
                  icon={
                    <SVGIcon
                      style={{
                        color: '@colorPrimary',
                      }}
                      icon={<AddIcon />}
                    />
                  }
                >
                  添加
                </Button>
                {fields.map(({ key, name, ...restField }) => (
                  <Form.Item key={key} {...restField} shouldUpdate>
                    <Flex gap={8} align="center">
                      <Form.Item name={[name, 'id']} hidden />
                      <Flex gap={8} vertical style={{ flex: 1 }}>
                        <Form.Item
                          noStyle
                          name={[name, 'competitorName']}
                          rules={[{ required: true, message: `请输入竞品名称` }]}
                        >
                          <Input placeholder={`请输入竞品名称`} />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          name={[name, 'introduce']}
                          rules={[{ required: true, message: `请输入竞品简介` }]}
                        >
                          <Input.TextArea
                            autoSize={{
                              minRows: 1,
                              maxRows: 8,
                            }}
                            placeholder={`请输入竞品简介`}
                          />
                        </Form.Item>
                      </Flex>
                      <SVGIcon
                        icon={<SVGDeleteTime />}
                        onClick={() => {
                          const currentId = form.getFieldValue(['competitors', name, 'id']);
                          if (currentId) {
                            setDeleteIds((prev) => [...prev, currentId]);
                          }
                          remove(name);
                        }}
                      />
                    </Flex>
                  </Form.Item>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </div>
    );
  }
);
export const openCreateArticleModal = CommonModalFactory<{
  selectedCoreWords?: string[];
  coreWords?: string[];
  locationWords?: string[];
  sceneWords?: string[];
  entityWords?: string[];
  companyId: string;
  competitors: {
    competitorName: string;
    id: string;
    introduce: string;
  }[];
  images?: CompanyImageVo[];
  strategy: 'MANUAL' | 'TEMPLATE';
}>(
  {
    title: '生成文章',
    width: 960,
    maskClosable: false,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const images = data?.images || [];
    const selectedImageIds = (Form.useWatch('imageIds', form) || []) as string[];
    const hasImages = images.length > 0;
    const isAllImagesSelected = hasImages && images.every((image) => selectedImageIds.includes(image.imageId));

    const selectAllImages = () => {
      form.setFieldValue(
        'imageIds',
        isAllImagesSelected ? [] : images.map((image) => image.imageId)
      );
    };

    const clearSelectedImages = () => {
      form.setFieldValue('imageIds', []);
    };

    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await articleApi.create({
          companyId: data?.companyId || '',
          ...values,
          strategy: data.strategy,
        });
      });
    }, [form, setOkFn, data.strategy]);
    return (
      <Form
        form={form}
        initialValues={{
          autoPublish: false,
          coreWords: data?.selectedCoreWords || data?.coreWords || [],
          categories: [
            {
              typeId: undefined,
              count: 4,
              mediaId: undefined,
            },
          ],
          locationWords: data?.locationWords || [],
          sceneWords: data?.sceneWords || [],
          entityWords: data?.entityWords?.length ? [data.entityWords[0]] : [],
          imageIds: [],
          templateId: undefined,
        }}
      >
        {data.strategy === 'TEMPLATE' && (
          <Form.Item name="templateId" label="发文模板" rules={[{ required: true, message: '请选择发文模板' }]}>
            <ArticleTemplateSelector />
          </Form.Item>
        )}
        <Form.Item name="autoPublish" label="是否自动发布">
          <Radio.Group
            options={[
              {
                value: true,
                label: '是',
              },
              {
                value: false,
                label: '否',
              },
            ]}
          />
        </Form.Item>
        <Form.Item name="coreWords" label="使用核心词" rules={[{ required: true, message: '请选择核心词' }]}>
          <CustomTreeSelect
            placeholder="请选择核心词"
            treeData={data?.coreWords?.map((word) => ({ label: word, value: word }))}
            treeCheckable
          />
        </Form.Item>
        <Form.Item name="locationWords" label="使用区位词">
          <CustomTreeSelect
            placeholder="请选择区位词"
            treeData={data?.locationWords?.map((word) => ({ label: word, value: word }))}
            treeCheckable
          />
        </Form.Item>
        <Form.Item name="sceneWords" label="使用场景词">
          <CustomTreeSelect
            placeholder="请选择场景词"
            treeData={data?.sceneWords?.map((word) => ({ label: word, value: word }))}
            treeCheckable
          />
        </Form.Item>
        <Form.Item name="entityWords" label="使用实体词">
          <CustomTreeSelect
            placeholder="请选择实体词"
            treeData={data?.entityWords?.map((word) => ({ label: word, value: word }))}
            treeCheckable
          />
        </Form.Item>
        {data.strategy === 'MANUAL' && (
          <>
            <Form.Item label="文章类型"></Form.Item>
            <Form.List name="categories">
              {(fields, { add, remove }) => (
                <div style={{ marginTop: '8px' }}>
                  {fields.map(({ key, name, ...restField }) => (
                    <Form.Item key={key} {...restField} shouldUpdate>
                      <Flex gap={8} align="flex-start">
                        <Flex gap={8} wrap style={{ flex: 1, minWidth: 0 }}>
                          <Form.Item
                            label={name === 0 ? '文章类型' : ''}
                            layout="vertical"
                            name={[name, 'typeId']}
                            rules={[{ required: true, message: `请选择文章类型` }]}
                            style={{ width: '220px', flexShrink: 0 }}
                          >
                            <ArticleTypeSelector />
                          </Form.Item>
                          <Form.Item
                            label={name === 0 ? '发文数量' : ''}
                            layout="vertical"
                            name={[name, 'count']}
                            rules={[{ required: true, message: `请输入发文数量` }]}
                          >
                            <InputNumber min={1} placeholder="发文数量" style={{ width: '120px', flexShrink: 0 }} />
                          </Form.Item>
                          <Form.Item
                            label={name === 0 ? '媒体' : ''}
                            layout="vertical"
                            name={[name, 'mediaId']}
                            style={{ flex: '1 1 420px', minWidth: 0 }}
                          >
                            <ChannelMediaBoxSelector />
                          </Form.Item>
                        </Flex>
                        <SVGIcon
                          icon={<SVGDeleteTime />}
                          style={{ marginTop: name === 0 ? 40 : 10 }}
                          onClick={() => {
                            remove(name);
                          }}
                        />
                      </Flex>
                    </Form.Item>
                  ))}
                  <Button
                    type="primary"
                    onClick={() => {
                      add({
                        typeId: undefined,
                        count: 4,
                        mediaId: undefined,
                      });
                    }}
                  >
                    添加
                  </Button>
                </div>
              )}
            </Form.List>
          </>
        )}
        <Form.Item name="competitorIds" label="使用竞品">
          <CustomTreeSelect
            placeholder="请选择竞品"
            treeData={data?.competitors?.map((row) => ({ label: row.competitorName, value: row.id }))}
            treeCheckable
          />
        </Form.Item>
        <Form.Item label="图片选择">
          <div className={`${styles.articleImagePanel} ${hasImages ? '' : styles.articleImagePanelDisabled}`}>
            <div className={styles.articleImageToolbar}>
              <span>{hasImages ? '从企业图片库中选择（可多选）' : '暂无数据'}</span>
              {hasImages && (
                <Button type="link" size="small" onClick={selectAllImages}>
                  {isAllImagesSelected ? '取消全选' : '全选'}
                </Button>
              )}
            </div>
            {hasImages ? (
              <Form.Item name="imageIds" noStyle>
                <Checkbox.Group className={styles.articleImageChooser}>
                  {images.map((image) => {
                    const selected = selectedImageIds.includes(image.imageId);
                    return (
                      <Checkbox
                        key={image.imageId}
                        value={image.imageId}
                        className={`${styles.articleImageOption} ${selected ? styles.articleImageOptionSelected : ''}`}
                      >
                        <img
                          className={styles.articleImageThumb}
                          src={image.thumbnailUrl || image.imageUrl}
                          alt={image.imageName}
                        />
                        <span className={styles.articleImageName} title={image.imageName}>
                          {image.imageName}
                        </span>
                      </Checkbox>
                    );
                  })}
                </Checkbox.Group>
              </Form.Item>
            ) : (
              <div className={styles.articleImageEmpty}>暂无数据</div>
            )}
          </div>
        </Form.Item>
        <div className={styles.articleImageFooter}>
          <span>已选择 {selectedImageIds.length} 张图片</span>
          {hasImages && (
            <Button type="link" size="small" disabled={!selectedImageIds.length} onClick={clearSelectedImages}>
              清空选择
            </Button>
          )}
        </div>
      </Form>
    );
  },
  async ({ companyId, ...props }) => {
    // 核心词
    const coreWords = await companyApi.getWordInfo(companyId);
    const locationWords = coreWords?.componentWords.find(
      (word) => word.type === ECompactWordType.LocationPrefix
    )?.words;
    const sceneWords = coreWords?.componentWords.find((word) => word.type === ECompactWordType.ScenePrefix)?.words;
    const entityWords = coreWords?.componentWords.find((word) => word.type === ECompactWordType.EntitySuffix)?.words;
    // 竞品列表
    const companyCompetitors = await articleApi.listCompetitors(companyId);
    const images = await companyApi.listImages(companyId);
    return {
      ...props,
      coreWords: coreWords?.coreWords.words || [],
      locationWords: locationWords || [],
      sceneWords: sceneWords || [],
      entityWords: entityWords || [],
      competitors: companyCompetitors || [],
      images: images || [],
      companyId,
    };
  }
);
